import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

type Props = {
  open: boolean;
  title: string;
  /** 作成先パス表示（例: /src/main）。未指定なら非表示 */
  pathLabel?: string;
  /** 拡張子の注記を表示するか（ファイル作成時） */
  showExtensionNote?: boolean;
  /** 作成可能拡張子の表示文字列（例: .java / .md） */
  extensionNote?: string;
  /**
   * 固定拡張子（例: 'java'）。指定するとファイル名の後ろに `.拡張子` を表示し、
   * 入力欄にはベース名のみを入力させる。onSubmit には「ベース名.拡張子」を渡す。
   */
  fixedExtension?: string;
  initialValue?: string;
  confirmLabel: string;
  /** サーバーエラー等のメッセージ */
  errorMessage?: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
};

/**
 * 新規作成 / リネーム用の名前入力ダイアログ（共通）。
 */
const NameInputDialog: React.FC<Props> = ({
  open,
  title,
  pathLabel,
  showExtensionNote,
  extensionNote,
  fixedExtension,
  initialValue = '',
  confirmLabel,
  errorMessage,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = React.useState(initialValue);

  React.useEffect(() => {
    if (open) {
      setName(initialValue);
    }
  }, [open, initialValue]);

  const handleSubmit = () => {
    if (!name.trim() || submitting) return;
    // 固定拡張子指定時はベース名に拡張子を付与して返す
    const value = fixedExtension ? `${name.trim()}.${fixedExtension}` : name.trim();
    onSubmit(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          elevation: 0,
          sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          {pathLabel && (
            <Typography variant="caption" color="text.secondary">
              作成先: {pathLabel}
            </Typography>
          )}
          <TextField
            autoFocus
            size="small"
            label={fixedExtension ? 'ファイル名' : '名前'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            slotProps={
              fixedExtension
                ? {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="text.secondary">
                            .{fixedExtension}
                          </Typography>
                        </InputAdornment>
                      ),
                    },
                  }
                : undefined
            }
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          {showExtensionNote && !fixedExtension && (
            <Typography variant="caption" color="text.secondary">
              ※ {extensionNote} のみ作成可能
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NameInputDialog;
